import { useCallback, useEffect, useRef, useState } from 'react';
import type { CameraSetup } from '@repo/frontend-types';
import type {
  OpeningViewPictureRequest,
  OpeningViewPictureResult,
} from '../../components/3D/planView/planPlacementRenderer';

/**
 * Position on map's side of the opening view's picture (`retire-edit-mode-opening-view`, R9): which
 * picture to ask the hidden map for, and what to show.
 *
 * - **One request per saved view and saved placement.** Its id is the twin's BID, the view's five
 *   values and the scene it is drawn over (`scene`, the saved position and orientation): a view or a
 *   placement committed after the read is a new request, and the same ones saved again are not.
 * - **Only the latest counts** (Failure States, *Overtaken*). A result for any other id is discarded
 *   and its object URL revoked.
 * - **The picture lives for the visit only.** It is an object URL over the PNG, revoked when replaced
 *   or when the step unmounts; it is never uploaded or stored.
 */
export const pictureRequestId = (twinBid: string, view: CameraSetup, scene = '') =>
  [twinBid, view.lat, view.lng, view.zoom, view.bearing, view.pitch, scene].join('|');

export type OpeningViewPictureShown =
  | { state: 'none' }
  | { state: 'drawing' }
  | { state: 'drawn'; url: string }
  | { state: 'failed' };

export interface OpeningViewPicture {
  /** The picture to ask for now; `null` once it has its answer, or with no view. */
  request: OpeningViewPictureRequest | null;
  shown: OpeningViewPictureShown;
  onResult: (result: OpeningViewPictureResult) => void;
}

export const useOpeningViewPicture = (
  twinBid: string,
  view: CameraSetup | null,
  /** A picture for the current view could not be drawn: said by the step (R8). */
  onFailed: () => void,
  /** What the picture is drawn over: a change to it is a new picture of the same view. */
  scene = '',
): OpeningViewPicture => {
  const id = view ? pictureRequestId(twinBid, view, scene) : null;
  const latestRef = useRef(id);
  latestRef.current = id;
  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;

  const [answer, setAnswer] = useState<
    { id: string; url: string } | { id: string; failed: true } | null
  >(null);
  /** The URL on screen, revoked when replaced or when the step goes. */
  const urlRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    },
    [],
  );

  const onResult = useCallback((result: OpeningViewPictureResult) => {
    const url = 'blob' in result ? URL.createObjectURL(result.blob) : null;
    if (result.id !== latestRef.current) {
      if (url) URL.revokeObjectURL(url);
      return;
    }
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    if (url) {
      setAnswer({ id: result.id, url });
    } else {
      setAnswer({ id: result.id, failed: true });
      onFailedRef.current();
    }
  }, []);

  const answered = !!answer && answer.id === id;
  const request = view && id && !answered ? { id, view } : null;
  const shown: OpeningViewPictureShown = !id
    ? { state: 'none' }
    : !answer || answer.id !== id
      ? { state: 'drawing' }
      : 'url' in answer
        ? { state: 'drawn', url: answer.url }
        : { state: 'failed' };
  return { request, shown, onResult };
};
